// @ts-nocheck
import React, { useState } from "react";
import { Badge } from "@/shared/components/ui/Badge";
import { Button, Input, Skeleton, useToast } from "@/shared/components/ui";
import { PrivacyModal } from "@/features/privacy/components/PrivacyModal";
import { PasswordField } from "./PasswordField";
import { validateAuthEmail } from "@/legacy/helpersModule";
/**
 * `fixedRole` pins the form to one audience and hides the Candidate/HR toggle,
 * so /login and /hr/login can be separate pages without duplicating any of the
 * submit, validation or success handling below. Omit it and the toggle returns,
 * i.e. the original combined behaviour.
 */
export function Login({ onCandSuccess, onHrSuccess, onRegister, onForgot, coolingMonths, dpo, initialEmail = "", fixedRole = null }) {
  const [role, setRole] = useState(fixedRole || "candidate"), [id, setId] = useState(initialEmail || ""), [pw, setPw] = useState(""), [hrPw, setHrPw] = useState(""), [busy, setBusy] = useState(false), [showP, setShowP] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const toast = useToast();
  const emailValid = role !== "candidate" || validateAuthEmail(id).ok;
  const submit = async () => {
    if (role === "candidate") {
      setEmailTouched(true);
      if (!id.trim() || !pw.trim()) return;
      const emailCheck = validateAuthEmail(id);
      if (!emailCheck.ok) {
        toast.show({ tone: "warning", message: emailCheck.message });
        return;
      }
    }
    if (role === "hr" && (!id.trim() || !hrPw.trim())) {
      toast.show({ tone: "warning", message: "Enter Corporate AD ID and password." });
      return;
    }
    setBusy(true);
    try {
      if (role === "hr") {
        const res = await fetch("/api/auth/login", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ role: "hr", hrId: id.trim(), password: hrPw }) });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) { toast.show({ tone: "danger", title: "Login failed", message: data.error || "HR login failed" }); return; }
        await onHrSuccess({ hrId: data.hrId || id.trim(), token: data.token || "cookie" });
      } else {
        const res = await fetch("/api/auth/login", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ role: "candidate", email: id.trim(), password: pw.trim() }) });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) { toast.show({ tone: "danger", title: "Login failed", message: data.error || "Invalid email or password" }); return; }
        await onCandSuccess({ candidateId: data.candidateId, token: data.token || "cookie" });
      }
    } catch (e) {
      toast.show({ tone: "danger", title: "Connection error", message: "Cannot reach server. Please try again." });
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {showP && <PrivacyModal onClose={() => setShowP(false)} coolingMonths={coolingMonths} dpo={dpo}/>}
      <div className="min-h-dvh safe-bottom bg-white flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto flex items-center justify-center mb-4"><img src="/aira-logo.png" alt="AIRA" className="w-full h-full object-contain" /></div>
            <h1 className="text-xl sm:text-3xl font-black text-ink-900">AIRA</h1>
            <p className="text-ink-400 text-sm mt-1">AI Recruitment Assistant</p>
            <div className="mt-2 flex justify-center"><Badge/></div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-8 shadow-sm">
            {fixedRole ? (
              /* Card header strip. Full-bleed divider (negative margins cancel the
                 card padding) so this reads as a header band rather than a label
                 with a rule under it — the toggle it replaced was a solid object,
                 and plain centred text left the card looking unfinished. */
              <div className="-mx-4 sm:-mx-8 px-4 sm:px-8 pb-4 mb-6 border-b border-slate-200 flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0" aria-hidden="true">
                  {fixedRole === "hr" ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3l7 3v5c0 4.418-2.867 8.166-7 9-4.133-.834-7-4.582-7-9V6l7-3z" /><path strokeLinecap="round" strokeLinejoin="round" d="M9.5 12l1.75 1.75L14.5 10.5" /></svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  )}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-ink-900 leading-tight">
                    {fixedRole === "hr" ? "HR Admin sign in" : "Candidate sign in"}
                  </p>
                  <p className="text-xs text-ink-400 leading-tight mt-0.5">
                    {fixedRole === "hr" ? "Recruitment team access" : "Sign in to continue your application"}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex rounded-control overflow-hidden mb-6 border border-slate-200">
                {["candidate", "hr"].map(r => <button key={r} disabled={busy} onClick={() => { setRole(r); setId(""); setPw(""); setHrPw(""); setEmailTouched(false); }} className={`flex-1 py-2.5 text-sm font-semibold capitalize transition-colors disabled:opacity-50 ${role === r ? "bg-brand-600 text-white" : "bg-white text-ink-400 hover:text-ink-900"}`}>{r === "hr" ? "HR Admin" : "Candidate"}</button>)}
              </div>
            )}
            {busy ? (
              <div className="space-y-4" aria-busy="true" aria-live="polite">
                <div className="flex items-center gap-3">
                  <span className="w-5 h-5 border-2 border-brand-300 border-t-brand-600 rounded-full animate-spin" />
                  <span className="text-ink-600 text-sm font-semibold">Logging you in…</span>
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-11 w-full rounded-control" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-11 w-full rounded-control" />
                </div>
                <Skeleton className="h-11 w-full rounded-control" />
              </div>
            ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-ink-600 uppercase tracking-wide mb-2">{role === "hr" ? "Corporate AD ID" : "Email"}</label>
                <Input
                  type={role === "hr" ? "text" : "email"}
                  value={id}
                  onChange={(e) => setId(e.target.value)}
                  onBlur={() => role === "candidate" && setEmailTouched(true)}
                  onKeyDown={(e) => e.key === "Enter" && submit()}
                  placeholder={role === "hr" ? "Email" : "your@email.com"}
                />
                {role === "candidate" && emailTouched && id.trim() && !emailValid ? (
                  <p className="text-xs text-brand-bad mt-1">Enter a valid email address.</p>
                ) : null}
              </div>
              {role === "candidate" && (
                <div>
                  <div className="flex items-center justify-between mb-2"><label className="text-xs font-semibold text-ink-600 uppercase tracking-wide">Password</label><button type="button" onClick={onForgot} className="text-xs text-brand-600 font-semibold hover:text-brand-700">Forgot?</button></div>
                  <PasswordField
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submit()}
                    placeholder="••••••••"
                  />
                </div>
              )}
              {role === "hr" && (
                <div>
                  <label className="block text-xs font-semibold text-ink-600 uppercase tracking-wide mb-2">Password</label>
                  <PasswordField
                    value={hrPw}
                    onChange={(e) => setHrPw(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submit()}
                    placeholder="Password"
                  />
                </div>
              )}
              <Button variant="primary" fullWidth onClick={submit} disabled={!id.trim() || (role === "candidate" && !pw.trim()) || (role === "hr" && !hrPw.trim())}>Sign In →</Button>
            </div>
            )}
            {role === "candidate" && (
              <div className="mt-6 pt-6 border-t border-slate-200 text-center space-y-2">
                <p className="text-ink-400 text-sm">New here? <button type="button" onClick={onRegister} className="text-brand-600 font-semibold hover:text-brand-700">Register</button></p>
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-slate-200 text-center"><button type="button" onClick={() => setShowP(true)} className="text-xs text-ink-400 underline hover:text-ink-600">Privacy Policy</button></div>
          </div>
        </div>
        <img
          src="/great-place-to-work.png"
          alt="Indira IVF — Great Place To Work Certified, Jan 2026–Jan 2027, India"
          className="hidden sm:block fixed fixed-safe-top-right w-16 md:w-20 h-auto z-10"
          loading="lazy"
        />
      </div>
    </>
  );
}
